import React from 'react';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { ShieldCheck as ShieldCheckIcon, Sparkles as SparklesIcon, ArrowRight as ArrowRightIcon } from 'lucide-react';

/**
 * ValidationCTA - Call-to-action for expert validation
 * Pixel-perfect port from Polymet validation-cta.tsx
 * 
 * @param {Object} props
 * @param {'subtle' | 'prominent'} [props.variant='subtle'] - CTA style variant
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.marketplaceUrl='/sprints/marketplace'] - URL to marketplace
 */
const ValidationCTA = ({
  variant = 'subtle',
  className = '',
  marketplaceUrl = '/sprints/marketplace',
}) => {
  const intl = useIntl();
  // Color constants for consistent styling
  const colors = {
    emerald50: '#ECFDF5',
    emerald100: '#D1FAE5',
    emerald200: '#A7F3D0',
    emerald500: '#10B981',
    emerald600: '#059669',
    emerald700: '#047857',
    emerald800: '#065F46',
    teal50: '#F0FDFA',
    teal500: '#14B8A6',
    teal600: '#0D9488',
    cyan50: '#ECFEFF',
  };

  if (variant === 'prominent') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`relative overflow-hidden rounded-xl border p-6 ${className}`}
        style={{
          borderColor: 'var(--succession-border, #E5E7EB)',
          background: `linear-gradient(to bottom right, ${colors.emerald50}80, ${colors.teal50}4D, ${colors.cyan50}80)`,
        }}
      >
        {/* Decorative background elements */}
        <div
          className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"
          style={{
            background: `linear-gradient(to bottom right, ${colors.emerald200}33, ${colors.teal500}33)`,
          }}
        />
        
        <div className="relative flex items-start gap-4">
          <div className="flex-shrink-0">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: `linear-gradient(to bottom right, ${colors.emerald500}, ${colors.teal600})`,
                boxShadow: `0 10px 15px -3px ${colors.emerald500}33`,
              }}
            >
              <ShieldCheckIcon className="w-6 h-6 text-white" />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.confidence_rating.validation_cta.prominent_title' })}
              </h3>
              <SparklesIcon className="w-4 h-4" style={{ color: colors.emerald600 }} />
            </div>

            <p className="text-sm text-succession-text-secondary mb-4">
              {intl.formatMessage({ id: 'succession.confidence_rating.validation_cta.prominent_description' })}
            </p>

            <div className="flex items-center gap-3">
              <a
                href={marketplaceUrl}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all"
                style={{
                  background: `linear-gradient(to right, ${colors.emerald600}, ${colors.teal600})`,
                  boxShadow: `0 4px 6px -1px ${colors.emerald500}33`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${colors.emerald700}, ${colors.teal600})`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${colors.emerald600}, ${colors.teal600})`;
                }}
              >
                {intl.formatMessage({ id: 'succession.confidence_rating.validation_cta.browse_programs_button' })}
                <ArrowRightIcon className="w-4 h-4" />
              </a>

              <button
                type="button"
                className="px-3 py-2 text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors"
                onClick={() => {/* Could open info modal */}}
              >
                {intl.formatMessage({ id: 'succession.confidence_rating.validation_cta.learn_more_button' })}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Subtle variant (default)
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`group relative overflow-hidden rounded-lg border p-4 transition-all duration-300 hover:shadow-md ${className}`}
      style={{
        borderColor: `${colors.emerald200}99`, // 60% opacity
        background: `linear-gradient(to right, ${colors.emerald50}66, ${colors.teal50}4D, ${colors.emerald50}66)`,
      }}
    >
      <div className="relative flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border"
            style={{
              background: `linear-gradient(to bottom right, ${colors.emerald500}1A, ${colors.teal500}1A)`,
              borderColor: `${colors.emerald200}80`,
            }}
          >
            <ShieldCheckIcon className="w-5 h-5" style={{ color: colors.emerald600 }} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h4 className="text-sm font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.confidence_rating.validation_cta.subtle_title' })}
              </h4>
              <SparklesIcon className="w-3.5 h-3.5" style={{ color: colors.emerald600 }} />
            </div>
            <p className="text-xs text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.confidence_rating.validation_cta.subtle_description' })}
            </p>
          </div>
        </div>
        
        <a
          href={marketplaceUrl}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
          style={{ color: colors.emerald700 }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = colors.emerald800;
            e.currentTarget.style.backgroundColor = `${colors.emerald100}80`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = colors.emerald700;
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {intl.formatMessage({ id: 'succession.confidence_rating.validation_cta.view_programs_button' })}
          <ArrowRightIcon className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </a>
      </div>
    </motion.div>
  );
};

export default ValidationCTA;
